'use client'
import Link from 'next/link'
import { ReadonlyURLSearchParams, usePathname, useSearchParams } from 'next/navigation'

import { ChevronLeft, ChevronRight, DoubleChevronLeft } from '@components/icons'

import s from './pagination.module.scss'

import { type JSX, Suspense } from 'react'

type PaginationProps = {
  totalPages: number
  currentPage: number
  pagesToDisplay?: number
}

const getPageUrl = (path: string, searchParams: ReadonlyURLSearchParams, page: number): string => {
  const newSearchparams = new URLSearchParams(searchParams)
  newSearchparams.set('page', String(page))
  return `${path}?${newSearchparams.toString()}`
}

function PaginationLinks({
  totalPages,
  currentPage,
  hrefFor,
}: PaginationProps & { hrefFor: (page: number) => string }): JSX.Element {
  /**
   * https://sonicjobs.atlassian.net/browse/CAN-257?focusedCommentId=25028
   */
  const adjustedTotalPages = Math.min(totalPages, 100)

  return (
    <div className={s.pagination}>
      {currentPage > 2 && (
        <Link className={s.icon} href={hrefFor(1)} aria-label="Go to first page">
          <DoubleChevronLeft className={s.icon} />
        </Link>
      )}
      {currentPage > 1 && (
        <Link className={s.icon} href={hrefFor(currentPage - 1)} aria-label="Go to previous page">
          <ChevronLeft className={s.icon} />
        </Link>
      )}
      <div className={s.inner}>
        <span>{currentPage}</span>
        <span>/</span>
        <span>{adjustedTotalPages}</span>
      </div>
      {currentPage < adjustedTotalPages && (
        <Link className={s.icon} href={hrefFor(currentPage + 1)} aria-label="Go to next page">
          <ChevronRight className={s.icon} />
        </Link>
      )}
    </div>
  )
}

function PaginationWithSearchParams(props: PaginationProps): JSX.Element {
  const searchParams = useSearchParams()
  const pathname = usePathname()

  return <PaginationLinks {...props} hrefFor={(page) => getPageUrl(pathname, searchParams, page)} />
}

export default function Pagination(props: PaginationProps): JSX.Element {
  // A page cached by ISR renders without a request, so useSearchParams suspends there. The fallback
  // still server-renders crawlable links: `?page=N` resolves against the current path, and the page is
  // the only query param the cached SERP uses [S20-994].
  return (
    <Suspense fallback={<PaginationLinks {...props} hrefFor={(page) => `?page=${page}`} />}>
      <PaginationWithSearchParams {...props} />
    </Suspense>
  )
}
